import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';

const ProfileEditor = ({ user, onProfileUpdated }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
  });
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      specialty: user?.specialty || '',
    });
  }, [user]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const toggleShowPassword = (field) => () => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
      };

      if (user?.role === 'practitioner') {
        payload.specialty = formData.specialty.trim();
      }

      const response = await api.put('/auth/profile', payload);
      onProfileUpdated?.(response.data.user);

      await SwalWithA11yFix({
        icon: 'success',
        title: 'Profile Updated',
        text: 'Your profile information has been updated.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      await SwalWithA11yFix({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Unable to update profile right now.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    const oldPassword = passwordData.oldPassword;
    const newPassword = passwordData.newPassword;
    const confirmPassword = passwordData.confirmPassword;

    if (!oldPassword || !newPassword || !confirmPassword) {
      await SwalWithA11yFix({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in old password, new password, and confirm password.',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      await SwalWithA11yFix({
        icon: 'warning',
        title: 'Password Mismatch',
        text: 'New password and confirm password do not match.',
      });
      return;
    }

    setPasswordSaving(true);
    try {
      await api.put('/auth/password', { oldPassword, newPassword });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswords({ oldPassword: false, newPassword: false, confirmPassword: false });
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Password Updated',
        text: 'Your password has been updated successfully.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      await SwalWithA11yFix({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Unable to update password right now.',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1b2a6b', fontWeight: 600 }}>
        Edit Profile
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField label="First Name" value={formData.firstName} onChange={handleInputChange('firstName')} required />
          <TextField label="Last Name" value={formData.lastName} onChange={handleInputChange('lastName')} required />
          <TextField label="Email" type="email" value={formData.email} onChange={handleInputChange('email')} required />
          <TextField label="Phone" value={formData.phone} onChange={handleInputChange('phone')} required />
          {user?.role === 'practitioner' && (
            <TextField label="Specialty" value={formData.specialty} onChange={handleInputChange('specialty')} />
          )}
          <Box>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 2, color: '#1b2a6b', fontWeight: 600 }}>
        Change Password
      </Typography>
      <Box component="form" onSubmit={handlePasswordSubmit}>
        <Stack spacing={2}>
          <TextField
            label="Old Password"
            type={showPasswords.oldPassword ? 'text' : 'password'}
            value={passwordData.oldPassword}
            onChange={handlePasswordChange('oldPassword')}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle old password visibility" onClick={toggleShowPassword('oldPassword')} edge="end">
                    {showPasswords.oldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="New Password"
            type={showPasswords.newPassword ? 'text' : 'password'}
            value={passwordData.newPassword}
            onChange={handlePasswordChange('newPassword')}
            autoComplete="new-password"
            helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle new password visibility" onClick={toggleShowPassword('newPassword')} edge="end">
                    {showPasswords.newPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm Password"
            type={showPasswords.confirmPassword ? 'text' : 'password'}
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange('confirmPassword')}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle confirm password visibility" onClick={toggleShowPassword('confirmPassword')} edge="end">
                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={passwordSaving}>
              {passwordSaving ? 'Updating...' : 'Update Password'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default ProfileEditor;
